import { useContext, useSyncExternalStore } from 'react';
import { AuthContext } from './AuthContext';
import { RolePreviewContext, type RolePreviewValue } from './RolePreviewContext';
import { getPreviewRole, previewChoicesFor, subscribePreview } from './role-preview';
import type { RoleToken } from './types';

const NOOP = () => {};

/**
 * The preview state. Outside a `RolePreviewProvider` (component tests,
 * the consoles) there is no preview: the auth context's roles are the
 * real ones and entering a preview does nothing.
 */
export function useRolePreview(): RolePreviewValue {
  const ctx = useContext(RolePreviewContext);
  const auth = useContext(AuthContext);
  if (ctx) return ctx;
  const realRoles = auth?.roles ?? [];
  return {
    previewRole: null,
    realRoles,
    choices: previewChoicesFor(realRoles),
    enterPreview: NOOP,
    exitPreview: NOOP,
  };
}

/**
 * The previewed role as the WRITE GATE sees it (module state), for
 * code that must agree with `assertWriteAllowed` rather than with the
 * provider's validated view -- e.g. a query `select` overriding a
 * server answer. Re-renders on change.
 */
export function usePreviewRole(): RoleToken | null {
  return useSyncExternalStore(subscribePreview, getPreviewRole, getPreviewRole);
}

/** True while a preview is active: actions are off. A cheap flag for
 *  disabling obvious action buttons; the client-level block in
 *  `apiFetch` is the guarantee, not this. */
export function useActionsOff(): boolean {
  return useRolePreview().previewRole !== null;
}
